/* InputNumberControlComponent.js */

"use strict";

const InputBaseControlTextfieldComponent = require("@gluenetworks/input_base_control").Textfield;
const Renderer = require("./InputNumberControlRenderer");
const _ = require("lodash");

class InputNumberControlComponent extends InputBaseControlTextfieldComponent {
    /**
     * @schema.name Input Number Control
     * @schema.package @gluenetworks/input_number_control
     * @schema.namespace require("@gluenetworks/steve_ui_core").Control.Textfield.InputNumber
     * @schema.events ["Min", "Max", "Placeholder", "Disabled", "Value", "Visible", "OnlyInteger", "Invalid", "DarkTheme", "Focus", "Blur",
     *                  "SetAttribute", "UserValue", "UserFocus", "UserBlur", "UserKeyDown", "Step"]
     *
     * @schema.configuration.onlyInteger.name onlyInteger
     * @schema.configuration.onlyInteger.type boolean
     * @schema.configuration.onlyInteger.default false
     * @schema.configuration.onlyInteger.required false
     *
     * @schema.configuration.step.name step
     * @schema.configuration.step.type integer|float|string
     * @schema.configuration.step.default
     * @schema.configuration.step.required false
     *
     * @schema.configuration.min.name min
     * @schema.configuration.min.type number
     * @schema.configuration.min.default undefined
     * @schema.configuration.min.required false
     *
     * @schema.configuration.max.name max
     * @schema.configuration.max.type number
     * @schema.configuration.max.default undefined
     * @schema.configuration.max.required false
     *
     */

    constructor(configOptions) {
        super(configOptions);

        this._min = isNaN(configOptions.min) ? undefined : configOptions.min;
        this._max = isNaN(configOptions.max) ? undefined : configOptions.max;
        this._onlyInteger = _.get(configOptions, "onlyInteger") === true ? true : false;
        this._step = this._onlyInteger ? 1 : "any";
        let step = _.get(configOptions, "step");
        if (this._isValidStep(step)) {
            this._step = _.isString(step) && step !== "any" ? parseFloat(step) : step;
        }

        this._renderer = new Renderer(this);

        this._registerListeners();
    }
    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/
    _registerListeners() {}

    _unregisterListeners() {}
    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/
    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }
    shutdown() {
        this._unregisterListeners();
        let prom = this._renderer._shutdown();
        this._renderer = undefined;
        this._disabled = undefined;
        this._onlyInteger = undefined;
        this._invalid = undefined;
        this._visible = undefined;
        this._value = undefined;
        this._darkTheme = undefined;
        this._theme = undefined;
        this._placeholder = undefined;
        this._DEFAULT_PLACEHOLDER = undefined;
        this._min = undefined;
        this._max = undefined;
        this._step = undefined;
        return prom;
    }
    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    /**
     * Verifies if given value is valid step on Input Number control
     * @param value
     * @returns {boolean}
     * @private
     */
    _isValidStep(value) {
        return !isNaN(value) || value === "any";
    }
    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    /**
     * @schema.methods.setOnlyInteger.name setOnlyInteger
     * @schema.methods.setOnlyInteger.description Sets if this input control should accept only integers
     * @schema.methods.setOnlyInteger.returns Instance of the control
     * @schema.methods.setOnlyInteger.parameters.newVal.name newVal
     * @schema.methods.setOnlyInteger.parameters.newVal.description Set to true if this control should accept only integers
     * @schema.methods.setOnlyInteger.parameters.newVal.type boolean
     */
    setOnlyInteger(newVal) {
        //if same as existing value, return
        if (newVal === this.onlyInteger) {
            return this;
        }

        this._onlyInteger = newVal === true;
        this.emit("OnlyInteger", this._onlyInteger);
        return this;
    }
    /**
     * @schema.methods.getOnlyInteger.name getOnlyInteger
     * @schema.methods.getOnlyInteger.description Returns if control accepts only integers
     * @schema.methods.getOnlyInteger.returns string
     */
    getOnlyInteger() {
        return this._onlyInteger;
    }
    get onlyInteger() {
        return this.getOnlyInteger();
    }
    set onlyInteger(newVal) {
        return this.setOnlyInteger(newVal);
    }

    /**
     * @schema.methods.setMin.name setMin
     * @schema.methods.setMin.description Sets the minimum allowed value for the control and returns an instance of the control for chainability
     * @schema.methods.setMin.returns Instance of the control
     * @schema.methods.setMin.parameters.newVal.name newVal
     * @schema.methods.setMin.parameters.newVal.description The minimum allowed value for the control
     * @schema.methods.setMin.parameters.newVal.type number
     */
    setMin(newVal) {
        /*eslint eqeqeq: 0 */
        if (newVal == this.min) {
            return;
        }
        this._min = isNaN(newVal) ? undefined : newVal;
        this.emit("Min", this._min);
        return this;
    }
    /**
     * @schema.methods.getMin.name getMin
     * @schema.methods.getMin.description Returns the minimum value of the control
     * @schema.methods.getMin.returns string
     */
    getMin() {
        return this._min;
    }
    get min() {
        return this.getMin();
    }
    set min(newVal) {
        return this.setMin(newVal);
    }

    /**
     * @schema.methods.setMax.name setMax
     * @schema.methods.setMax.description Sets the maximum allowed value for the control and returns an instance of the control for chainability
     * @schema.methods.setMax.returns Instance of the control
     * @schema.methods.setMax.parameters.newVal.name newVal
     * @schema.methods.setMax.parameters.newVal.description The maximum allowed value for the control
     * @schema.methods.setMax.parameters.newVal.type number
     */
    setMax(newVal) {
        if (newVal == this.max) {
            return;
        }
        this._max = isNaN(newVal) ? undefined : newVal;
        this.emit("Max", this._max);
        return this;
    }
    /**
     * @schema.methods.getMax.name getMax
     * @schema.methods.getMax.description Returns the maximum value of the control
     * @schema.methods.getMax.returns string
     */
    getMax() {
        return this._max;
    }
    get max() {
        return this.getMax();
    }
    set max(newVal) {
        return this.setMax(newVal);
    }

    /**
     * @schema.methods.setStep.name setStep
     * @schema.methods.setStep.description Set step value on input control
     * @schema.methods.setStep.returns Instance of the control
     * @schema.methods.setStep.parameters.newVal.name newVal
     * @schema.methods.setStep.parameters.newVal.description Step value of input control
     * @schema.methods.setStep.parameters.newVal.type number|string
     */
    setStep(newVal) {
        // if same as current step value, do nothing
        if (this._step == newVal) {
            return this;
        }

        if (this._isValidStep(newVal)) {
            let newStepVal = newVal;
            if (_.isString(newStepVal) && newStepVal !== "any") {
                newStepVal = parseFloat(newStepVal);
            }
            this._step = newStepVal;
            this.emit("Step", newStepVal);
        } else {
            this._step = undefined;
        }
        return this;
    }

    /**
     * @schema.methods.getStep.name getStep
     * @schema.methods.getStep.description Returns step value on input control
     * @schema.nethods.getStep.returns integer|float|string
     * @returns {*}
     */
    getStep() {
        return this._step;
    }

    get step() {
        return this.getStep();
    }

    set step(value) {
        return this.setStep(value);
    }

    /**
     * @schema.methods.getValidationMessages.name getValidationMessages
     * @schema.methods.getValidationMessages.description Returns list of validation error messages if the text value doesn't
     *          match given regular expression (pattern)
     * @schema.methods.getValidationMessages.returns {Array}
     * @returns {*}
     */
    getValidationMessages() {
        //returns clone of validationMessages array
        return this._validationMessages.slice(0);
    }

    get validationMessages() {
        return this.getValidationMessages();
    }

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}
module.exports = InputNumberControlComponent;
